import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, Table, TablePaginationConfig, TableProps } from 'antd';
import React, { useState, useMemo } from 'react';

interface CTableProps {
  title: string;
  columns: TableProps<any>['columns'];
  data: any[];
  pagination?: false | TablePaginationConfig;
  addBtnTitle?: string;
  loading?: boolean;
  hideSearch?: boolean;
  searchFields?: string[];
  primarySearchField?: string;
  // When provided, search is CONTROLLED by the parent (server-side): the input
  // reports keystrokes via onSearch and CTable skips its own client-side filtering.
  onSearch?: (value: string) => void;
  searchValue?: string;
  onAdd?: () => void;
  tableProps?: TableProps<any>;
  onRow?: (record: any) => any;
  filterComponent?: React.ReactNode;
  infoComponent?: React.ReactNode;
  headerRight?: React.ReactNode;
  hasMore?: boolean;
  disableAddButton?: boolean;
  // Hide the add button entirely (e.g. the user lacks the create permission),
  // as opposed to disableAddButton which greys it out.
  hideAddButton?: boolean;
  handledScroll?: boolean;
  loadMore?: () => void;
  onRowClick?: (record: any) => void;
  rowClassName?: (record: any, index?: number) => string;
  // Custom node rendered inside the table when there are no rows (e.g. an
  // empty-state with a call-to-action). Falls back to the default antd empty.
  emptyText?: React.ReactNode;
}

const CTable = (props: CTableProps) => {
  const [searchText, setSearchText] = useState('');

  const primarySearchField = props.primarySearchField || 'name';

  const searchFields = useMemo(
    () => props.searchFields || [primarySearchField],
    [primarySearchField, props.searchFields],
  );
  const filteredData = useMemo(() => {
    // Server-controlled search: parent already returns the matching page.
    if (props.onSearch) return props.data;
    if (!searchText) return props.data;

    return props.data.filter((record) => {
      return searchFields.some((field) => {
        const fieldValue = record[field];
        if (typeof fieldValue === 'string') {
          return fieldValue.toLowerCase().includes(searchText.toLowerCase());
        }
        return String(fieldValue)
          .toLowerCase()
          .includes(searchText.toLowerCase());
      });
    });
  }, [props.data, props.onSearch, searchText, searchFields]);

  // const items = [
  //   {
  //     title: 'Dashboard',
  //     href: 'dashboard',
  //   },
  //   {
  //     title: 'Admin',
  //     href: '#',
  //   },
  //   {
  //     title: props.title,
  //     href: props.title.toLowerCase(),
  //   },
  // ];
  const handleRowClick = (record: any) => {
    if (props.onRowClick) {
      props.onRowClick(record);
    }
  };
  const shouldApplyNegativeMargin =
    props.title || props.headerRight || !props.hideSearch;
  const marginTopClass = shouldApplyNegativeMargin ? 'mt-[-50px]' : '';

  return (
    <div className="flex flex-col px-5 ctable">
      <div
        className={`flex flex-col sm:flex-row pl-3 pb-4 gap-4 ${marginTopClass}`}
      >
        <div className="flex gap-3 items-center mr-auto" />
        <div className="flex flex-col sm:flex-row gap-3">
          {props.headerRight}
          {!props.headerRight && <div className="h-8" />}
          {!props.hideSearch && (
            <Input
              placeholder={`Search ${primarySearchField}`}
              className="w-full sm:w-[300px]"
              value={props.onSearch ? props.searchValue : searchText}
              onChange={(e) =>
                props.onSearch
                  ? props.onSearch(e.target.value)
                  : setSearchText(e.target.value)
              }
              allowClear
              prefix={<SearchOutlined className="text-gray-400 w-[20px]" />}
            />
          )}

          {props.addBtnTitle && !props.hideAddButton && (
            <Button
              disabled={props.disableAddButton}
              type="primary"
              icon={<PlusOutlined />}
              onClick={props.onAdd}
            >
              {props.addBtnTitle}
            </Button>
          )}
        </div>
      </div>

      <div className={'mb-5'}>
        {props.filterComponent && props.filterComponent}
      </div>
      <div className={'mb-5'}>{props.infoComponent && props.infoComponent}</div>
      <Table
        loading={props.loading}
        columns={props.columns}
        dataSource={filteredData}
        locale={props.emptyText ? { emptyText: props.emptyText } : undefined}
        pagination={props.pagination}
        scroll={{
          x: 'max-content',
          y: props.handledScroll ? 'calc(100vh - 360px)' : undefined,
        }}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
        })}
        rowClassName={props.rowClassName}
        footer={
          props.hasMore
            ? () => (
                <div className={'flex justify-center item-center'}>
                  <Button onClick={props.loadMore}>Load more</Button>
                </div>
              )
            : undefined
        }
      />
    </div>
  );
};

export default CTable;
